import React, { useEffect, useState } from "react";
import UserAvatarIcon from "../../../img/user_avatar_gray.svg";
import EscapeOutside from "react-escape-outside";
import { userService } from "../../UserList/services/userService";
import LoadMorePagination from "../../InventoryManagement/components/Common/LoadMorePagination";
import EmptyCard from "../../InventoryManagement/components/Common/EmptyCard";

const UserLogSkeletonCard = () => (
  <div className="user-log-card user-log-card--skeleton">
    <div className="user-log-message-row">
      <div
        className="skeleton-shimmer"
        style={{ width: "55%", height: 18, borderRadius: 4 }}
      />
      <div
        className="skeleton-shimmer"
        style={{ width: 130, height: 16, borderRadius: 4 }}
      />
    </div>

    <div className="user-log-editor-line user-log-editor-line--skeleton">
      <div
        className="skeleton-shimmer"
        style={{ width: 180, height: 14, borderRadius: 4 }}
      />
    </div>
  </div>
);

const UserLogs = ({ userId }) => {
  const [focusedEditor, setFocusedEditor] = useState(null);
  const [page, setPage] = useState(0);
  const [logs, setLogs] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(5);

  useEffect(() => {
    setFocusedEditor(null);
    setPage(0);
    setLogs([]);
    setTotalCount(0);
    setInitialLoading(false);
    setLoadMoreLoading(false);
  }, [userId]);

  useEffect(() => {
    const fetchUserLogs = async () => {
      if (!userId) return;

      try {
        if (page === 0) {
          setInitialLoading(true);
        } else {
          setLoadMoreLoading(true);
        }

        const data = await userService.getUserLogs(userId, page);

        if (page === 0) {
          setLogs(data.data || []);
        } else {
          setLogs((prev) => [...prev, ...(data.data || [])]);
        }

        setTotalCount(data.count || 0);
      } catch (err) {
        console.error("Error fetching user logs:", err);
      } finally {
        setInitialLoading(false);
        setLoadMoreLoading(false);
      }
    };

    fetchUserLogs();
  }, [page, userId]);

  const toggleEditorCard = (index) => {
    setFocusedEditor((prev) => (prev === index ? null : index));
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="user-logs-panel">
      {initialLoading ? (
        <div className="user-log-list">
          {Array.from({ length: 3 }).map((_, index) => (
            <UserLogSkeletonCard key={index} />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="user-logs-empty-state">
          <EmptyCard
            title="No Logs Available"
            message="There are no logs to display for this user."
          />
        </div>
      ) : (
        <div className="user-log-list">
          {logs.map((log, index) => (
            <div key={index} className="user-log-card">
              <div className="user-log-message-row">
                <p className="user-log-message">{log.message}</p>
                <span className="user-log-time">{log.created_at}</span>
              </div>

              <div className="user-log-editor-line">
                <span className="user-log-editor-label">Edited by </span>
                <button
                  type="button"
                  className="user-log-editor-link"
                  onClick={() => toggleEditorCard(index)}
                >
                  {log.edited_by.name}
                </button>
              </div>

              {focusedEditor === index && (
                <EscapeOutside onEscapeOutside={() => setFocusedEditor(null)}>
                  <div className="user-log-editor-card">
                    {log.edited_by.image ? (
                      <img
                        src={log.edited_by.image}
                        alt={`${log.edited_by.name}'s profile`}
                        className="user-log-editor-avatar"
                      />
                    ) : (
                      <img
                        src={UserAvatarIcon}
                        alt="Default avatar"
                        className="user-log-editor-avatar"
                      />
                    )}
                    <div className="user-log-editor-info">
                      <div className="user-log-editor-name">
                        {log.edited_by.name}
                      </div>
                      <div className="user-log-editor-email">
                        {log.edited_by.email}
                      </div>
                    </div>
                  </div>
                </EscapeOutside>
              )}
            </div>
          ))}
        </div>
      )}

      {logs.length > 0 && totalPages > 1 && (
        <LoadMorePagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          isLoadingMore={loadMoreLoading}
          style={{
            margin: "20px 0px 0px",
          }}
        />
      )}
    </div>
  );
};

export default UserLogs;
